import styled from "styled-components";
import Modal from "../modal";
import { Button } from "melodies-source/Button";
import { useState } from "react";
import { useDesktopMediaQuery } from "hooks";
import { CircularLoader, useArtistContext } from "Components";
import { TextInput } from "melodies-source/TextInput";
import CheckCircleIcon from "../CheckCircleIcon";
import { Ref } from "refs";
import { addDoc, Timestamp, updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

type FlowStatus =
  | "addApiKey"
  | "testingApiKey"
  | "apiKeyAdded"
  | "syncExistingContacts";

type OnPitchIntegrationModalProps = {
  artistGroupId: string;
  isOpen: boolean;
  onClose: () => void;
  overrideStatus?: FlowStatus;
};

export const OnPitchIntegrationModal = ({
  artistGroupId,
  isOpen,
  onClose,
  overrideStatus,
}: OnPitchIntegrationModalProps) => {
  const [status, setStatus] = useState<FlowStatus>(
    overrideStatus || "addApiKey"
  );
  const [apiAddError, setApiAddError] = useState("");
  const [apiKey, setApiKey] = useState("");
  const { logAction } = useArtistContext();

  const isDesktop = useDesktopMediaQuery();

  const onSubmitApiKey = async () => {
    setApiAddError("");
    setStatus("testingApiKey");

    try {
      const response = await httpsCallable(
        getFunctions(),
        "services-onPitchActions"
      )({
        action: "connect",
        artistGroupId,
        apiKey,
      });

      if (!response.data) {
        throw new Error("Error testing api key, please verify input.");
      }

      await logAction("portal_connect_onpitch");

      setStatus("apiKeyAdded");
    } catch (error) {
      console.error(error);

      const errorMessage =
        error instanceof Error ? error.message : String(error);
      setApiAddError(errorMessage);

      setStatus("addApiKey");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} service="onpitch">
      {status === "addApiKey" && (
        <>
          <HeaderText isDesktop={isDesktop}>Create API Key</HeaderText>
          <br />
          <BodyText isDesktop={isDesktop}>
            Please create a private API key and enter it below. Please do not
            share your key via email; our system will encrypt your key:
          </BodyText>
          <br />
          <TextInput
            placeholder="Enter API Key"
            value={apiKey}
            onChange={(text) => setApiKey(text)}
            hasError={!!apiAddError}
          />
          {!!apiAddError && (
            <ErrorText isDesktop={isDesktop}>{apiAddError}</ErrorText>
          )}
          <br />
          <FooterText isDesktop={isDesktop}>
            If you need help with creating an API key, contact OnPitch customer
            support.
          </FooterText>
          <br />
          <StyledButton disabled={!apiKey} onClick={onSubmitApiKey}>
            Add key and test connection
          </StyledButton>
        </>
      )}
      {status === "testingApiKey" && (
        <>
          <CircularLoader
            height={isDesktop ? 60 : 41}
            sx={{ marginBottom: 10 }}
          />
          <BodyText isDesktop={isDesktop}>Please wait...</BodyText>
          <BodyText isDesktop={isDesktop}>
            We're adding the API Key and testing the connection
          </BodyText>
        </>
      )}
      {status === "apiKeyAdded" && (
        <>
          <CheckCircleIcon />
          <BodyText isDesktop={isDesktop}>Success!</BodyText>
          <BodyText isDesktop={isDesktop}>
            Connection stable and API Key added
          </BodyText>

          <Button
            style={{ marginTop: 20 }}
            onClick={() => onBackfillClickHandler(artistGroupId, onClose)}
          >
            Sync all existing fans to OnPitch
          </Button>
        </>
      )}
      {status === "syncExistingContacts" && (
        <>
          <BodyText isDesktop={isDesktop}>Sync Existing Contacts?</BodyText>
          <Button
            style={{ marginTop: 32 }}
            onClick={() => onBackfillClickHandler(artistGroupId, onClose)}
          >
            Sync all existing fans to OnPitch
          </Button>
        </>
      )}
    </Modal>
  );
};

const onBackfillClickHandler = async (
  artistGroupId: string,
  onClose: () => void
) => {
  try {
    await addDoc(
      Ref("artist_groups/{groupId}/artist_group_private/onpitch/backfills", {
        groupId: artistGroupId,
      }),
      { backfillRequest: Timestamp.now() }
    );

    await updateDoc(
      Ref("artist_groups/{groupId}/artist_group_private/onpitch", {
        groupId: artistGroupId,
      }),
      {
        audienceConnected: true,
      }
    );
  } catch (error) {
    console.error((error as Error).message);
  } finally {
    onClose();
  }
};

export const OnPitchRemovalModal = ({
  artistGroupId,
  onClose,
  isOpen,
}: {
  artistGroupId: string;
  onClose: () => void;
  isOpen: boolean;
}) => {
  const [removingOnPitch, setRemovingOnPitch] = useState(false);
  const [removalError, setRemovalError] = useState("");
  const { logAction } = useArtistContext();

  const isDesktop = useDesktopMediaQuery();

  const onRemoveOnPitch = async () => {
    setRemovingOnPitch(true);

    setRemovalError("");

    try {
      await httpsCallable(
        getFunctions(),
        "services-onPitchActions"
      )({ action: "remove", artistGroupId });

      await logAction("portal_remove_onpitch");

      onClose();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.error(errorMessage);

      setRemovalError(`Error removing OnPitch account: ${errorMessage}`);
      setRemovingOnPitch(false);
    }
  };

  const modalBody = () => {
    if (removalError) {
      return (
        <HeaderText style={{ color: "#e45c52" }} isDesktop={isDesktop}>
          {removalError}
        </HeaderText>
      );
    }

    if (removingOnPitch) {
      return (
        <>
          <CircularLoader height={30} sx={{ marginRight: "0.5rem" }} />
          <br />
          <BodyText isDesktop={isDesktop}>Removing OnPitch account...</BodyText>
        </>
      );
    }
    return (
      <HeaderText isDesktop={isDesktop}>
        Are you sure you want to unlink your OnPitch account?
      </HeaderText>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} service="onpitch">
      {modalBody()}
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          marginTop: 40,
        }}
      >
        <Button
          onClick={() => onRemoveOnPitch()}
          style={{ marginRight: "2rem" }}
          disabled={removingOnPitch}
        >
          Confirm
        </Button>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={removingOnPitch}
        >
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

const BodyText = styled.div<{ isDesktop: boolean }>`
  font-weight: 400;
  font-size: ${({ isDesktop }) => (isDesktop ? "17px" : "12px")};
  line-height: ${({ isDesktop }) => (isDesktop ? "25.5px" : "18px")};
`;

const ErrorText = styled.div<{ isDesktop: boolean }>`
  color: #e45c52;
  font-weight: 500;
  font-size: ${({ isDesktop }) => (isDesktop ? "min(1.3vw, 12px)" : "13px")};
  line-height: 19.5px;
  margin-top: 5px;
  align-self: flex-start;
  margin-left: 10px;
`;

const FooterText = styled.div<{ isDesktop: boolean }>`
  font-weight: 500;
  font-size: ${({ isDesktop }) => (isDesktop ? "13px" : "10px")};
  line-height: 15px;
`;

const HeaderText = styled.div<{ isDesktop: boolean }>`
  font-weight: 600;
  font-size: ${({ isDesktop }) => (isDesktop ? "20px" : "17px")};
  line-height: ${({ isDesktop }) => (isDesktop ? "30px" : "25.5px")};
`;

const StyledButton = styled(Button)`
  max-width: 385px;
`;
